import { useState, useEffect } from 'react';
import type { Brand, Lead } from '../types';
import { getApiUrl } from '../config/api';

// Partners get a reduced lead shape from the API — details only, no CRM fields
type PartnerLead = Pick<Lead,
  'id' | 'created_at' | 'brand_id' | 'vertical' | 'paper_size' | 'bw_volume' |
  'colour_volume' | 'estimated_total' | 'company_name' | 'contact_name' |
  'email' | 'phone' | 'postcode' | 'existing_provider'
>;

export function PartnerLeadsPage() {
  const [leads, setLeads] = useState<PartnerLead[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState<PartnerLead | null>(null);
  const [brandFilter, setBrandFilter] = useState('all');

  useEffect(() => {
    fetch(getApiUrl('/api/leads?limit=200'), { credentials: 'include' })
      .then(r => r.ok ? r.json() : { leads: [] })
      .then(d => setLeads(d.leads || []))
      .catch(() => {})
      .finally(() => setLoading(false));

    fetch(getApiUrl('/api/brands'), { credentials: 'include' })
      .then(r => r.ok ? r.json() : { brands: [] })
      .then(d => setBrands(d.brands || []))
      .catch(() => {});
  }, []);

  const brandName = (id: string) => brands.find(b => b.id === id)?.name || id;

  // Only offer filters for brands this partner actually has leads from
  const leadBrandIds = Array.from(new Set(leads.map(l => l.brand_id)));
  const visibleLeads = brandFilter === 'all' ? leads : leads.filter(l => l.brand_id === brandFilter);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Your Leads</h1>
        <p className="text-dark-text-secondary text-sm sm:text-base">
          Leads generated for your company from your assigned sites
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <div className="card p-4">
          <div className="text-dark-text-secondary text-xs mb-1">Total Leads</div>
          <div className="text-white text-2xl font-bold">{leads.length}</div>
        </div>
        <div className="card p-4">
          <div className="text-green-400 text-xs mb-1">Last 7 Days</div>
          <div className="text-white text-2xl font-bold">
            {leads.filter(l => Date.now() - new Date(l.created_at).getTime() < 7 * 24 * 60 * 60 * 1000).length}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-blue-400 text-xs mb-1">Sites</div>
          <div className="text-white text-2xl font-bold">{leadBrandIds.length}</div>
        </div>
      </div>

      {/* Brand filter */}
      {leadBrandIds.length > 1 && (
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <label className="text-sm font-medium text-dark-text">Filter by site:</label>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setBrandFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  brandFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-dark-bg text-dark-text hover:bg-slate-700'
                }`}
              >
                All
              </button>
              {leadBrandIds.map(id => (
                <button
                  key={id}
                  onClick={() => setBrandFilter(id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    brandFilter === id ? 'bg-blue-600 text-white' : 'bg-dark-bg text-dark-text hover:bg-slate-700'
                  }`}
                >
                  {brandName(id)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Leads Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-dark-text-secondary">Loading leads...</div>
        ) : visibleLeads.length === 0 ? (
          <div className="p-8 text-center text-dark-text-secondary">
            No leads yet — new leads will appear here as soon as they come in
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-dark-bg border-b border-dark-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-text-secondary uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-text-secondary uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-text-secondary uppercase tracking-wider">Requirements</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-text-secondary uppercase tracking-wider">Site</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-text-secondary uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-text-secondary uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-border">
                {visibleLeads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-dark-text">{lead.company_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-dark-text">{lead.contact_name}</div>
                      <div className="text-xs text-dark-text-secondary">{lead.email}</div>
                      <div className="text-xs text-dark-text-secondary">{lead.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-dark-text">
                        {lead.paper_size} · {lead.bw_volume.toLocaleString()} B&W · {lead.colour_volume.toLocaleString()} Colour
                      </div>
                      {lead.estimated_total && (
                        <div className="text-xs text-dark-text-secondary">
                          Est: £{lead.estimated_total.toFixed(2)}/month
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-dark-text">{brandName(lead.brand_id)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-text-secondary">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button onClick={() => setSelectedLead(lead)} className="btn btn-secondary text-sm">
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lead Detail Modal (read-only) */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="card p-4 sm:p-6 w-full max-w-3xl my-2 sm:my-0">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white">{selectedLead.company_name}</h2>
                <p className="text-dark-text-secondary">Lead #{selectedLead.id}</p>
              </div>
              <button
                onClick={() => setSelectedLead(null)}
                className="text-dark-text-secondary hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-white mb-3">Contact Information</h3>
                <div>
                  <label className="text-xs text-dark-text-secondary">Contact Name</label>
                  <p className="text-dark-text">{selectedLead.contact_name}</p>
                </div>
                <div>
                  <label className="text-xs text-dark-text-secondary">Email</label>
                  <p className="text-dark-text">{selectedLead.email}</p>
                </div>
                <div>
                  <label className="text-xs text-dark-text-secondary">Phone</label>
                  <p className="text-dark-text">{selectedLead.phone}</p>
                </div>
                {selectedLead.postcode && (
                  <div>
                    <label className="text-xs text-dark-text-secondary">Postcode</label>
                    <p className="text-dark-text">{selectedLead.postcode}</p>
                  </div>
                )}
                {selectedLead.existing_provider && (
                  <div>
                    <label className="text-xs text-dark-text-secondary">Existing Provider</label>
                    <p className="text-dark-text">{selectedLead.existing_provider}</p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-bold text-white mb-3">Requirements</h3>
                <div>
                  <label className="text-xs text-dark-text-secondary">Product Type</label>
                  <p className="text-dark-text">{selectedLead.paper_size}</p>
                </div>
                <div>
                  <label className="text-xs text-dark-text-secondary">Monthly Volume</label>
                  <p className="text-dark-text">
                    {selectedLead.bw_volume.toLocaleString()} B&W · {selectedLead.colour_volume.toLocaleString()} Colour
                  </p>
                </div>
                {selectedLead.estimated_total && (
                  <div>
                    <label className="text-xs text-dark-text-secondary">Estimated Total</label>
                    <p className="text-dark-text text-lg font-bold">£{selectedLead.estimated_total.toFixed(2)}/month</p>
                  </div>
                )}
                <div>
                  <label className="text-xs text-dark-text-secondary">Site</label>
                  <p className="text-dark-text">{brandName(selectedLead.brand_id)}</p>
                </div>
                <div>
                  <label className="text-xs text-dark-text-secondary">Received</label>
                  <p className="text-dark-text">{new Date(selectedLead.created_at).toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button onClick={() => setSelectedLead(null)} className="btn btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
